// src/hooks/useIncompleteRegistration.ts
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { RegistrationType } from '../types/registration';

export const useIncompleteRegistration = (type: RegistrationType) => {
    const [incompleteRecord, setIncompleteRecord] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);

    const checkIncompleteRecord = async () => {
        try {
            setLoading(true);

            // For carriers, skip the incomplete registration check
            // Carriers register for free and complete in one step
            // Duplicate handling is done in the form submission
            if (type === 'carrier') {
                setLoading(false);
                return;
            }

            const { data: userData, error: userError } = await supabase.auth.getUser();
            
            if (userError) {
                console.error('Error getting user:', userError);
                setLoading(false);
                return;
            }

            if (!userData?.user) {
                setLoading(false);
                return;
            }

            // Ensure user_id is properly formatted
            const userId = userData.user.id;
            if (!userId) {
                setLoading(false);
                return;
            }

            const tableName = 'quotes';

            let query = supabase
                .from(tableName)
                .select('*')
                .eq('user_id', userId)
                .order('created_at', { ascending: false })
                .limit(1);

            // For quotes, look for unpaid ones only
            // The modal should only appear if the client hasn't paid (service_paid = false)
            // regardless of the delivery/transport status in the carrier panel
            query = query.eq('service_paid', false);

            const { data, error } = await query.maybeSingle();

            if (error) {
                if (error.code === 'PGRST116') {
                    // No rows found - this is expected
                    setLoading(false);
                    return;
                } else {
                    console.error(`Error checking incomplete record in ${tableName}:`, error);
                    setLoading(false);
                    return;
                }
            }

            if (data) {
                setIncompleteRecord({
                    id: data.id,
                    data,
                    type
                });
                setShowDialog(true);
            }

            setLoading(false);
        } catch (error) {
            console.error('Unexpected error in checkIncompleteRecord:', error);
            setLoading(false);
        }
    };

    const continueWithPrevious = () => {
        setShowDialog(false);
        return incompleteRecord;
    };

    const discardAndCreateNew = async () => {
        if (!incompleteRecord) return null;

        try {
            const tableName = incompleteRecord.type === 'quote' ? 'quotes' : 'carrier_registrations';

            const { error } = await supabase
                .from(tableName)
                .delete()
                .eq('id', incompleteRecord.id);

            if (error) {
                console.error('Error deleting incomplete record:', error);
                return null;
            }

            setIncompleteRecord(null);
            setShowDialog(false);
            return null;
        } catch (error) {
            console.error('Error in discardAndCreateNew:', error);
            return null;
        }
    };

    useEffect(() => {
        checkIncompleteRecord();
    }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        incompleteRecord,
        loading,
        showDialog,
        continueWithPrevious,
        discardAndCreateNew
    };
};